import React from 'react';
import { Users, Edit, UserCheck, UserX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import { EmployeesTableProps } from './EmployeesTable.types';
import { EmployeeStatus } from '../../types/employee.types';
import { getPositionColor } from '../../hooks/usePositions';
import { Employee } from '../../types/employee.types';

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-PE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const getStatusText = (status: EmployeeStatus): string => {
  switch (status) {
    case 'active':
      return 'Activo';
    case 'inactive':
      return 'Inactivo';
    case 'suspended':
      return 'Suspendido';
    default:
      return status;
  }
};

const getStatusBadgeClass = (status: EmployeeStatus): string => {
  switch (status) {
    case 'active':
      return 'bg-green-100 text-green-700';
    case 'inactive':
      return 'bg-gray-100 text-gray-700';
    case 'suspended':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

// Removed hardcoded getPositionColor function - now imported from usePositions hook

const getNextStatus = (currentStatus: EmployeeStatus): EmployeeStatus => {
  return currentStatus === 'active' ? 'inactive' : 'active';
};

type SortField = 'firstName' | 'email' | 'dni' | 'position' | 'status' | 'hireDate';

export const EmployeesTable: React.FC<EmployeesTableProps> = ({
  employees,
  onEdit,
  onToggleStatus
}) => {
  // Configuración del hook reutilizable
  const fieldLabels: Record<SortField, string> = {
    firstName: 'Empleado',
    email: 'Email',
    dni: 'DNI',
    position: 'Cargo',
    status: 'Estado',
    hireDate: 'Fecha Ingreso'
  };

  // Extractor personalizado para campos complejos
  const sortValueExtractor = (employee: Employee, field: SortField): any => {
    switch (field) {
      case 'firstName':
        return `${employee.firstName} ${employee.lastName}`.toLowerCase();
      case 'email':
        return employee.email.toLowerCase();
      case 'dni':
        return employee.dni;
      case 'position':
        return employee.position.name.toLowerCase();
      case 'status':
        return employee.status;
      case 'hireDate':
        return new Date(employee.hireDate);
      default:
        return employee.firstName;
    }
  };

  // Usar el hook reutilizable
  const tableData = useTableData<Employee, SortField>(
    {
      data: employees,
      defaultSortField: 'firstName',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (employees.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empleados</h3>
        <p className="text-gray-500">No se encontraron empleados con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="firstName"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Empleado
              </SortableHeader>
              <SortableHeader
                field="email"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Email
              </SortableHeader>
              <SortableHeader
                field="dni"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                DNI
              </SortableHeader>
              <SortableHeader
                field="position"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Cargo
              </SortableHeader>
              <SortableHeader
                field="status"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Estado
              </SortableHeader>
              <SortableHeader
                field="hireDate"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Fecha Ingreso
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((employee) => {
              return (
                <tr key={employee.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {employee.firstName} {employee.lastName}
                      </div>
                      <div className="text-xs text-gray-400">
                        ID: {employee.id}
                      </div>
                    </div>
                  </td>
                  
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900">
                      {employee.email}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900 font-medium">
                      {employee.dni}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <Badge
                      variant="outline"
                      className={`${getPositionColor(employee.position.level || 'support')} border-0`}
                    >
                      {employee.position.name}
                    </Badge>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <Badge className={getStatusBadgeClass(employee.status)}>
                      {getStatusText(employee.status)}
                    </Badge>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="text-sm text-gray-900">
                      {formatDate(employee.hireDate)}
                    </div>
                  </td>
                  
                  <td className="px-4 py-4 text-center">
                    <div className="flex gap-2 justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(employee)}
                        className="text-xs"
                      >
                        <Edit className="w-3 h-3 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onToggleStatus(employee.id, getNextStatus(employee.status))}
                        className={`text-xs ${
                          employee.status === 'active'
                            ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                            : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                        }`}
                      >
                        {employee.status === 'active' ? (
                          <UserX className="w-3 h-3 mr-1" />
                        ) : (
                          <UserCheck className="w-3 h-3 mr-1" />
                        )}
                        {employee.status === 'active' ? 'Desactivar' : 'Activar'}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};